import React from 'react';
import Search from '@mui/icons-material/Search';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Block from '@mui/icons-material/Block';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import Flight from '@mui/icons-material/Flight';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  DEFAULT_LOG_SEARCH_OPTIONS,
  selectActivityLogsSearchStatus,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  setLogSearch,
  setLogSearchOptions,
  setLogSearches,
} from '../redux';
import { hasActiveLogSearch } from '../logSearch';
import LogSearchOptionsMenu from './LogSearchOptionsMenu';
import * as S from './ActivitySelector.styled';

const MAX_SEARCHES = 5;

type ActivityLogsDraftSearchProps = {
  /** Wider input when embedded in the right-pane bar. */
  barLayout?: boolean,
  /** Share a row with Time / Cash chips (Search activity card). */
  inline?: boolean,
};

/** Draft search row — text field, options, and add chip. */
const ActivityLogsDraftSearch = ({ barLayout, inline }: ActivityLogsDraftSearchProps) => {
  const dispatch = useAppDispatch();
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const activityLogsSearchStatus = useAppSelector(selectActivityLogsSearchStatus);

  const draftTrimmed = logSearch.trim();
  const canAddSearch = Boolean(draftTrimmed) && logSearches.length < MAX_SEARCHES;
  const draftNegate = logSearchOptions.negate;
  const hasAnySearch = hasActiveLogSearch(logSearches, logSearch);

  const clearAllSearch = () => {
    dispatch(setLogSearches([]));
    dispatch(setLogSearch(''));
    dispatch(setLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS }));
  };

  const commitSearch = () => {
    if (!canAddSearch) return;
    dispatch(setLogSearches([
      ...logSearches,
      { term: draftTrimmed, options: { ...logSearchOptions } },
    ]));
    dispatch(setLogSearch(''));
    dispatch(setLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS }));
  };

  const draftIcon = () => {
    if (logSearchOptions.matchMode === 'room') return <MeetingRoomOutlined fontSize="medium" />;
    if (logSearchOptions.matchMode === 'staff') return <PersonOutline fontSize="medium" />;
    if (logSearchOptions.matchMode === 'customer') return <PeopleOutline fontSize="medium" />;
    if (logSearchOptions.matchMode === 'department') return <AccountTreeOutlined fontSize="medium" />;
    if (logSearchOptions.matchMode === 'ota') return <Flight fontSize="medium" />;
    if (logSearchOptions.fromFullLogs) return <LibraryAdd fontSize="medium" />;
    if (draftNegate) return <Block fontSize="medium" />;
    return <Search fontSize="medium" />;
  };

  const draftPlaceholder = () => {
    if (logSearches.length) {
      if (logSearchOptions.fromFullLogs) {
        return draftNegate ? 'Exclude from prior results…' : 'Add from prior results…';
      }
      return draftNegate ? 'Exclude from results…' : 'Refine search…';
    }
    if (logSearchOptions.matchMode === 'room') {
      return draftNegate ? 'Exclude room name…' : 'Search by room name';
    }
    if (logSearchOptions.matchMode === 'staff') {
      return draftNegate ? 'Exclude staff…' : 'Search by staff username';
    }
    if (logSearchOptions.matchMode === 'customer') {
      return draftNegate ? 'Exclude guest…' : 'Search by guest';
    }
    if (logSearchOptions.matchMode === 'department') {
      return draftNegate ? 'Exclude department…' : 'Search by department';
    }
    if (logSearchOptions.matchMode === 'ota') {
      return draftNegate ? 'Exclude OTA…' : 'Search by OTA';
    }
    return draftNegate
      ? (activityLogsSearchStatus === 'results' ? 'Exclude from current logs…' : 'Exclude from activity logs…')
      : (activityLogsSearchStatus === 'results' ? 'Search current logs…' : 'Enter an activity search(optional)');
  };

  return (
    <S.SearchRow $barLayout={barLayout} $inline={inline}>
      <S.SearchInputWrap>
        {!logSearch ? (
          <S.IconDiv>
            {draftIcon()}
          </S.IconDiv>
        ) : null}
        <S.Input
          $hasClear={Boolean(logSearch)}
          $hideLeftIcon={Boolean(logSearch)}
          placeholder={draftPlaceholder()}
          value={logSearch}
          onChange={(e) => {
            dispatch(setLogSearch(e.target.value));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canAddSearch) {
              e.preventDefault();
              commitSearch();
            }
          }}
        />
        {logSearch ? (
          <S.ClearSearchBtn
            type="button"
            aria-label="Clear search"
            title="Clear search"
            onClick={() => dispatch(setLogSearch(''))}
          >
            <Close fontSize="inherit" />
          </S.ClearSearchBtn>
        ) : null}
      </S.SearchInputWrap>
      <LogSearchOptionsMenu
        options={logSearchOptions}
        onChange={(next) => {
          const safe = logSearches.length === 0 && next.fromFullLogs
            ? { ...next, fromFullLogs: false }
            : next;
          dispatch(setLogSearchOptions(safe));
        }}
        canClearAll={hasAnySearch}
        onClearAll={clearAllSearch}
        allowFromFullLogs={logSearches.length > 0}
      />
      {canAddSearch && (
        <S.AddSearchBtn
          type="button"
          aria-label="Add search filter"
          title="Add search filter"
          onClick={commitSearch}
        >
          <Add fontSize="inherit" />
        </S.AddSearchBtn>
      )}
    </S.SearchRow>
  );
};

export default ActivityLogsDraftSearch;
